import { QueryClient } from '@tanstack/react-query';
import { ApiError } from './api';

/**
 * Sensible defaults:
 * - 5s staleTime keeps reads responsive without hammering the API.
 * - Don't retry 4xx client errors (auth, validation, RBAC). Only network/5xx.
 * - Per-query `refetchInterval` is used to poll in-progress FSMs (5s),
 *   wired up in the feature hooks.
 */
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5_000,
      gcTime: 5 * 60_000,
      refetchOnWindowFocus: false,
      retry: (failureCount, err) => {
        if (err instanceof ApiError && err.status >= 400 && err.status < 500) {
          return false;
        }
        return failureCount < 2;
      },
    },
    mutations: {
      retry: false,
    },
  },
});

/**
 * Statuses considered terminal across the four FSM-bearing entities
 * (audit, keyword run, brief, report). Used by polling hooks.
 */
export const TERMINAL_STATUSES = new Set([
  'completed',
  'failed',
  'cancelled',
  'delivered',
  'needs_review',
]);

export function isTerminal(status: string | null | undefined): boolean {
  if (!status) return false;
  return TERMINAL_STATUSES.has(status);
}
